import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useEffect, useState, useMemo } from "react";
import { loginRequest } from "../../../../AuthConfig";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { useProfileRepository } from "../../../../data/repositories/ProfileRepository";
import { useCartContext } from "../../../../contexts/CartContext";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { OrderState } from "../../../screens/order-list/vmHooks/useFunctionalListOrdersViewModel";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { enviroment } from "../../../../enviroment";

export interface NavigationBarViewModel {
  login: () => void;
  logout: () => void;
  userRole?: Role;
  companyLogo?: string;
  pendingOrdersCount?: number;
}

export function useNavigationBarViewModel(): NavigationBarViewModel {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { profile } = useProfileContext();
  const { clearProfile } = useProfileRepository();
  const { clearItems } = useCartContext();
  const { orderRepository } = useRepositoriesContext();
  const [role, setRole] = useState<Role>();
  const [companyLogo] = useState<string>();

  // Use React Query to fetch orders. Behind the gateOrdersBadge flag, gate the fetch to Vendors —
  // the only role that sees the pending-orders badge (NavigationBar renders it for userRole === Vendor).
  // Admins/customers skip this heavy pageSize=1000 fetch with no visible change. Flag off = always fetch.
  const ordersEnabled = enviroment.gateOrdersBadge
    ? isAuthenticated &&
      profile?.userOrganizationInformations?.role === Role.Vendor
    : true;
  const { data: ordersData } = orderRepository.useAllOrders(
    {
      searchTerm: "",
      filterBy: [],
      sortBy: {
        field: "",
      },
      page: { index: 0, size: 1000 },
    },
    ordersEnabled
  );

  // Calculate pending orders count using useMemo to prevent infinite re-renders
  const pendingOrdersCount = useMemo(() => {
    if (
      isAuthenticated &&
      profile &&
      profile.userOrganizationInformations.role >= Role.Vendor &&
      ordersData
    ) {
      const pendingOrders = ordersData.results.filter((order) =>
        isOrderAwaitingVendorApproval(order)
      );
      return pendingOrders.length;
    }
    return undefined;
  }, [isAuthenticated, profile, ordersData]);

  useEffect(() => {
    if (isAuthenticated && profile) {
      setRole(profile.userOrganizationInformations.role);
    }
  }, [isAuthenticated, profile]);

  function isOrderAwaitingVendorApproval(order: any): boolean {
    // Check if order is in Pending state and has vendor approval pending
    return (
      order.status === OrderState.Pending &&
      order.approval?.vendor?.status === ApprovalStatus.Pending
    );
  }

  function login() {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest);
    }
  }

  function logout() {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      instance.logout();
      clearProfile();
      clearItems();
    }
  }

  return {
    login,
    logout,
    userRole: role,
    companyLogo,
    pendingOrdersCount,
    // customer
  };
}
